// @aiContributed-MAPFuse-2026-03-25
import { useContext, useEffect, useMemo, useRef } from 'react';
import { odata } from '@bingads-webui/http-util';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { edmCampaign } from '@bingads-webui/edm-campaign';

import { useOdataOptions } from './use-odata-options';

const readAudienceCriterions = ({
  cid,
  aid,
  campaignId,
  adGroupId,
}) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AdGroups.$withKey(adGroupId)
    .AudienceAssociations
    .get({ top: 2000 })
    .toPromise();

export const useTargetingCacheWarmUp = ({ enabled = false, campaignId, adGroupId } = {}) => {
  const serverContext = useContext(ServerContext);
  const { ServicesEndpoints } = (serverContext as any) || {};
  const odataUrl = ServicesEndpoints && ServicesEndpoints.CampaignAPI;
  const { hostname } = new URL(odataUrl || '', window.location.href);
  const isOnebox = hostname === 'app1.localhost.ccui.devdev.bingads.microsoft.com' ||
    /(^|\.)redmond\.corp\.microsoft\.com$/i.test(hostname);

  // Match the fixed path override used by multi-ad-format repository prefetch.
  const pathOverride = isOnebox ? '/Advertiser/V2' : 'Campaign/V2';

  const [OdataOptions, { aid, cid }] = useOdataOptions(pathOverride);

  const startedRef = useRef(false);
  const audienceCriterionsWarmUpKeyRef = useRef();

  const audienceOptions = useMemo(() => ({
    ...OdataOptions,
    url: `Customers(${cid})/Accounts(${aid})/Audiences`,
    data: {
      $orderby: 'Name',
      audiencetype: 'InMarket',
      deliverychannel: 'AIM',
    },
    cache: true,
  }), [OdataOptions, aid, cid]);

  const demographicBaseUrl = useMemo(
    () => `Customers(${cid})/Accounts(${aid})/Demographics/`,
    [aid, cid]
  );

  const jobFunctionOptions = useMemo(() => ({
    ...OdataOptions,
    url: `${demographicBaseUrl}Model.JobFunction`,
    cache: true,
  }), [OdataOptions, demographicBaseUrl]);

  const industryOptions = useMemo(() => ({
    ...OdataOptions,
    url: `${demographicBaseUrl}Model.Industry`,
    cache: true,
  }), [OdataOptions, demographicBaseUrl]);

  const shouldWarmUpAudienceCriterions = enabled && Boolean(campaignId && adGroupId);
  const audienceCriterionsWarmUpKey = shouldWarmUpAudienceCriterions
    ? `${cid}:${aid}:${campaignId}:${adGroupId}`
    : undefined;

  useEffect(() => {
    const fireAndForget = (promiseFactory) => {
      try {
        const promise = promiseFactory();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(() => undefined);
        }
      } catch (e) {
        // Swallow: warm-up should never block the caller.
      }
    };

    if (enabled && !startedRef.current) {
      startedRef.current = true;

      fireAndForget(() => odata.get(audienceOptions));
      fireAndForget(() => odata.get(jobFunctionOptions));
      fireAndForget(() => odata.get(industryOptions));
    }

    if (shouldWarmUpAudienceCriterions && audienceCriterionsWarmUpKeyRef.current !== audienceCriterionsWarmUpKey) {
      audienceCriterionsWarmUpKeyRef.current = audienceCriterionsWarmUpKey;

      fireAndForget(() => readAudienceCriterions({
        cid,
        aid,
        campaignId,
        adGroupId,
      }));
    }
  }, [
    adGroupId, aid, audienceCriterionsWarmUpKey, audienceOptions, campaignId,
    cid, enabled, industryOptions, jobFunctionOptions, shouldWarmUpAudienceCriterions,
  ]);
};
